import React, { Component, ErrorInfo, ReactNode } from 'react';
import { createNotification } from '../../utils/dom';
import { trackError, ErrorSeverity } from '../../utils/error-tracking';
import { hardNavigate } from '../../utils/hard-navigate';
import {
  captureErrorBoundaryException,
  getErrorBoundaryNotificationMessage,
} from './errorUtils';

export interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  eventId: string | null;
  errorCount: number;
  lastErrorTime: number;
}

export interface BaseErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode | ((error: Error, errorInfo: ErrorInfo, retry: () => void) => ReactNode);
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  resetKeys?: Array<string | number>;
  resetOnPropsChange?: boolean;
  isolate?: boolean;
  level?: 'page' | 'section' | 'component';
  showDetails?: boolean;
  enableRetry?: boolean;
  maxRetries?: number;
  retryDelay?: number;
  silentFallback?: boolean;
}

export class BaseErrorBoundary extends Component<BaseErrorBoundaryProps, ErrorBoundaryState> {
  private resetTimeoutId: NodeJS.Timeout | null = null;
  private retryCount = 0;

  constructor(props: BaseErrorBoundaryProps) {
    super(props);

    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      eventId: null,
      errorCount: 0,
      lastErrorTime: 0,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
      error,
      lastErrorTime: Date.now(),
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const { onError, level = 'component', silentFallback } = this.props;

    // Vercel edge cache failures — the /_next/data JSON fetch 500s but the
    // page itself works. Reset the boundary so the component can mount and
    // use its own API fallback to load data.
    if (error.message?.includes('Failed to load static props') ||
        error.message?.includes('Failed to load script')) {
      this.resetErrorBoundary();
      return;
    }

    trackError(error, ErrorSeverity.ERROR, {
      source: 'error-boundary',
      metadata: {
        level,
        componentStack: errorInfo.componentStack,
      },
    } as unknown as Parameters<typeof trackError>[2]);

    // Send notification unless it's a silent error
    if (!silentFallback) {
      const notificationMessage = getErrorBoundaryNotificationMessage(level);
      createNotification({
        type: 'error',
        msg: notificationMessage,
        dismissed: false,
      });
    }

    // Capture to ClickHouse with error boundary context
    captureErrorBoundaryException(error, errorInfo, {
      level,
      retryCount: this.retryCount,
      additionalContext: {
        errorCount: this.state.errorCount + 1,
      },
    });

    this.setState((prevState) => ({
      errorInfo,
      eventId: null, // No longer using Sentry eventId
      errorCount: prevState.errorCount + 1,
    }));

    // Call custom error handler if provided
    onError?.(error, errorInfo);
  }

  componentDidUpdate(prevProps: BaseErrorBoundaryProps) {
    const { resetKeys, resetOnPropsChange } = this.props;
    const { hasError } = this.state;

    // Reset error boundary if resetKeys changed
    if (hasError && resetKeys && prevProps.resetKeys) {
      const hasResetKeyChanged = resetKeys.some((key, idx) => key !== prevProps.resetKeys![idx]);
      if (hasResetKeyChanged) {
        this.resetErrorBoundary();
      }
    }

    // Reset on any props change if enabled
    if (hasError && resetOnPropsChange && prevProps.children !== this.props.children) {
      this.resetErrorBoundary();
    }
  }

  /** A fresh start (new reset keys, new children): the retry budget starts over. */
  resetErrorBoundary = () => {
    this.retryCount = 0;
    this.clearError();
  };

  /**
   * Render the children again, keeping the retry count. Retries used to go
   * through resetErrorBoundary, which zeroed the count each time, so a child
   * that kept failing could be retried forever, every attempt said "1 of 3",
   * and the button never showed its count or went away.
   */
  private clearError = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      eventId: null,
    });
  };

  handleRetry = () => {
    const { maxRetries = 3, retryDelay = 1000 } = this.props;

    if (this.retryCount < maxRetries) {
      this.retryCount++;

      // Clear any existing timeout
      if (this.resetTimeoutId) {
        clearTimeout(this.resetTimeoutId);
      }

      // Show retry notification
      createNotification({
        type: 'info',
        msg: `Retrying... (Attempt ${this.retryCount} of ${maxRetries})`,
        dismissed: false,
      });

      // Delay retry to avoid rapid retries
      this.resetTimeoutId = setTimeout(this.clearError, retryDelay * Math.min(this.retryCount, 3));
    }
  };

  renderErrorFallback() {
    const {
      fallback,
      showDetails = process.env.NODE_ENV === 'development',
      enableRetry = true,
      maxRetries = 3,
      silentFallback = false,
      level = 'component',
    } = this.props;

    const { error, errorInfo } = this.state;

    if (silentFallback) {
      return null;
    }

    if (fallback && error && errorInfo) {
      if (typeof fallback === 'function') {
        return fallback(error, errorInfo, this.handleRetry);
      }
      return fallback;
    }

    // Default error UI with Tailwind CSS
    return (
      <div className={`
        flex flex-col items-center justify-center p-8
        ${level === 'page' ? 'min-h-screen' : level === 'section' ? 'min-h-[400px]' : 'min-h-[200px]'}
        bg-gray-50 rounded-lg border border-red-200
      `}>
        <div className="text-center max-w-md">
          <div className="mb-4">
            <svg
              className="mx-auto h-12 w-12 text-red-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>

          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Something went wrong
          </h2>

          <p className="text-gray-600 mb-6">
            {error?.message || 'An unexpected error occurred. Please try again.'}
          </p>

          {showDetails && errorInfo && (
            <details className="mb-6 text-left">
              <summary className="cursor-pointer text-sm text-gray-500 hover:text-gray-700">
                Error details
              </summary>
              <div className="mt-2 p-3 bg-gray-100 rounded text-xs text-gray-700 overflow-auto max-h-40">
                <pre className="whitespace-pre-wrap">{error?.stack}</pre>
                <pre className="whitespace-pre-wrap mt-2">{errorInfo.componentStack}</pre>
              </div>
            </details>
          )}

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            {enableRetry && this.retryCount < maxRetries && (
              <button
                onClick={this.handleRetry}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Try again {this.retryCount > 0 && `(${this.retryCount}/${maxRetries})`}
              </button>
            )}

            {level === 'page' && (
              <button
                onClick={() => hardNavigate('/')}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Go home
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  render() {
    if (this.state.hasError) {
      if (this.props.isolate) {
        // Return error UI without propagating error up
        return this.renderErrorFallback();
      }

      // Re-throw to let parent boundaries handle if not isolated
      if (!this.props.fallback && !this.props.silentFallback) {
        throw this.state.error;
      }

      return this.renderErrorFallback();
    }

    return this.props.children;
  }
}

export default BaseErrorBoundary;